import type { Knex } from 'knex'
import path from 'node:path'
import fs from 'node:fs/promises'
import { generateId } from '../../core/utils/id.js'
import { getDb } from './connection.js'
import { logger } from '@kernel/logger/index.js'
import { getMigrationsDir } from '@kernel/config/paths.js'
import type { Logger } from 'pino'
import { acquireMigrationLock, releaseMigrationLock } from './migration-lock.js'
import { buildMigrationSources, type MigrationSourceDescriptor, type MigrationSourceId } from './migration-sources.js'

/**
 * Migration file interface.
 * Each migration file must export up() and down() functions.
 */
export interface MigrationFile {
  name: string
  filepath: string
  source: MigrationSourceId
  up: (knex: Knex) => Promise<void>
  down: (knex: Knex) => Promise<void>
  config?: { transaction?: boolean }
}

/**
 * Migration record in _nexus_migrations table.
 */
export interface MigrationRecord {
  id: string
  name: string
  batch: number
  status: 'running' | 'completed' | 'failed' | 'rolled_back'
  source: MigrationSourceId
  executed_at: Date | null
  rolled_back_at: Date | null
  execution_time_ms: number | null
  error: string | null
  created_at: Date
  updated_at: Date
}

/**
 * Load migration files from all sources (core → plugins → project).
 * Validates naming conventions and detects name collisions.
 */
export async function loadAllMigrationFiles(
  sources?: MigrationSourceDescriptor[]
): Promise<MigrationFile[]> {
  const effectiveSources = sources ?? buildMigrationSources()
  const allMigrations: MigrationFile[] = []
  const nameSet = new Set<string>()

  // Collect all non-empty prefixes to filter project source in dev mode
  // (when core/plugin dirs overlap with project dir)
  const reservedPrefixes = effectiveSources
    .filter(s => s.prefix)
    .map(s => s.prefix)

  for (const source of effectiveSources) {
    const files = await loadMigrationFilesFromDir(source.dir, source.id)

    for (const migration of files) {
      // Non-project sources: skip files that don't match their prefix
      if (source.prefix && !migration.name.startsWith(source.prefix)) {
        logger.warn(
          { file: migration.name, source: source.id, expectedPrefix: source.prefix },
          'Migration file name does not match expected prefix — skipping'
        )
        continue
      }

      // Project source: skip files that belong to core/plugin sources (by prefix)
      if (!source.prefix && reservedPrefixes.some(p => migration.name.startsWith(p))) {
        continue
      }

      // Detect name collisions across sources
      if (nameSet.has(migration.name)) {
        throw new Error(
          `Migration name collision: "${migration.name}" exists in multiple sources. ` +
          `Each migration name must be globally unique.`
        )
      }

      nameSet.add(migration.name)
      allMigrations.push(migration)
    }
  }

  return allMigrations
}

/**
 * Load migration files from a single directory.
 */
async function loadMigrationFilesFromDir(
  dir: string,
  source: MigrationSourceId
): Promise<MigrationFile[]> {
  try {
    await fs.access(dir)
  } catch {
    return []
  }

  const files = await fs.readdir(dir)
  const migrations: MigrationFile[] = []

  for (const file of files.sort()) {
    if (!file.endsWith('.ts') && !file.endsWith('.js')) continue

    const name = file.replace(/\.(ts|js)$/, '')
    const filepath = path.join(dir, file)

    try {
      const module = await import(filepath)

      if (typeof module.up !== 'function' || typeof module.down !== 'function') {
        throw new Error(`Invalid migration file: ${file}. Must export up() and down()`)
      }

      migrations.push({
        name,
        filepath,
        source,
        up: module.up,
        down: module.down,
        config: module.config,
      })
    } catch (err) {
      logger.error({ file, source, err }, 'Failed to load migration file')
      throw err
    }
  }

  return migrations
}

/**
 * Run all pending migrations from files (multi-source).
 * @param knexInstance - Optional Knex instance (uses getDb() if not provided)
 * @param sources - Optional migration sources (uses buildMigrationSources() if not provided)
 */
export async function runMigrations(
  knexInstance?: Knex,
  sources?: MigrationSourceDescriptor[]
): Promise<void> {
  const knex = knexInstance ?? getDb()

  await acquireMigrationLock(knex, { timeout: 60000 })

  try {
    const migrationFiles = await loadAllMigrationFiles(sources)

    // Get already-executed migration names
    const executedMigrations = await knex('_nexus_migrations')
      .where({ status: 'completed' })
      .select('name')
      .then((rows) => new Set(rows.map((r) => r.name)))

    const pendingMigrations = migrationFiles.filter((m) => !executedMigrations.has(m.name))

    if (pendingMigrations.length === 0) {
      logger.debug('No pending migrations')
      return
    }

    const batch = await getNextBatch(knex)

    logger.info({ count: pendingMigrations.length, batch }, 'Running migrations')

    for (const migration of pendingMigrations) {
      await runMigration(knex, migration, batch, logger)
    }

    const total = await getCompletedCount(knex)
    logger.info({ batch, total }, 'Migrations completed')
  } finally {
    await releaseMigrationLock(knex)
  }
}

/**
 * Run a single migration.
 */
async function runMigration(
  knex: Knex,
  migration: MigrationFile,
  batch: number,
  logger: Logger
): Promise<void> {
  const startTime = Date.now()

  // Check for existing record (rolled_back or failed) to avoid UNIQUE constraint
  const existing = await knex('_nexus_migrations')
    .where({ name: migration.name })
    .whereIn('status', ['rolled_back', 'failed'])
    .first()

  let id: string
  if (existing) {
    id = existing.id
    await knex('_nexus_migrations').where({ id }).update({
      batch,
      status: 'running',
      source: migration.source,
      error: null,
      updated_at: knex.fn.now(),
    })
  } else {
    id = generateId()
    await knex('_nexus_migrations').insert({
      id,
      name: migration.name,
      batch,
      status: 'running',
      source: migration.source,
      created_at: knex.fn.now(),
      updated_at: knex.fn.now(),
    })
  }

  try {
    // Respect migration config.transaction (default: true)
    // FK changes on SQLite require PRAGMA foreign_keys = OFF which cannot run inside a transaction
    if (migration.config?.transaction === false) {
      await migration.up(knex)
    } else {
      await knex.transaction(async (trx) => {
        await migration.up(trx)
      })
    }

    const executionTime = Date.now() - startTime
    await knex('_nexus_migrations').where({ id }).update({
      status: 'completed',
      executed_at: knex.fn.now(),
      execution_time_ms: executionTime,
      updated_at: knex.fn.now(),
    })

    logger.info({ migration: migration.name, source: migration.source, time: executionTime }, 'Migration applied')
  } catch (err) {
    await knex('_nexus_migrations').where({ id }).update({
      status: 'failed',
      error: err instanceof Error ? err.message : String(err),
      updated_at: knex.fn.now(),
    })

    logger.error({ migration: migration.name, source: migration.source, err }, 'Migration failed')
    throw err
  }
}

/**
 * Rollback last batch of migrations.
 * Loads from all sources to find down() functions.
 * @param knexInstance - Optional Knex instance (uses getDb() if not provided)
 * @param source - Optional source filter (e.g. 'core', 'plugin:@gzl10/nexus-plugin-tags', 'project')
 * @param sources - Optional migration sources for loading files (uses buildMigrationSources() if not provided)
 */
export async function rollbackLastBatch(knexInstance?: Knex, source?: MigrationSourceId, sources?: MigrationSourceDescriptor[]): Promise<void> {
  const knex = knexInstance ?? getDb()

  await acquireMigrationLock(knex)

  try {
    const maxQuery = knex('_nexus_migrations')
      .max('batch as batch')
      .where({ status: 'completed' })
    if (source) maxQuery.where({ source })

    const result = await maxQuery.first()
    const lastBatch = result?.['batch']

    if (!lastBatch) {
      logger.info(source ? { source } : {}, 'No migrations to rollback')
      return
    }

    const batchQuery = knex('_nexus_migrations')
      .where({ batch: lastBatch, status: 'completed' })
      .orderBy('executed_at', 'desc')
    if (source) batchQuery.where({ source })

    const migrations = await batchQuery.select<MigrationRecord[]>()

    logger.info({ batch: lastBatch, count: migrations.length, ...(source && { source }) }, 'Rolling back batch')

    // Load from all sources to find down() functions
    const migrationFiles = await loadAllMigrationFiles(sources)
    const migrationMap = new Map(migrationFiles.map((m) => [m.name, m]))

    for (const record of migrations) {
      const migration = migrationMap.get(record.name)
      if (!migration) {
        logger.warn({ migration: record.name }, 'Migration file not found, marking as rolled back')
        await knex('_nexus_migrations').where({ id: record.id }).update({
          status: 'rolled_back',
          rolled_back_at: knex.fn.now(),
          updated_at: knex.fn.now(),
        })
        continue
      }

      await rollbackMigration(knex, migration, record, logger)
    }
  } finally {
    await releaseMigrationLock(knex)
  }
}

/**
 * Rollback a specific migration by name.
 * @param name - Migration name (e.g. '20240315_001_create_users')
 * @param knexInstance - Optional Knex instance (uses getDb() if not provided)
 */
export async function rollbackByName(name: string, knexInstance?: Knex): Promise<void> {
  const knex = knexInstance ?? getDb()

  await acquireMigrationLock(knex)

  try {
    const record = await knex('_nexus_migrations')
      .where({ name, status: 'completed' })
      .first<MigrationRecord>()

    if (!record) {
      throw new Error(`Migration "${name}" not found or not in completed state`)
    }

    const migrationFiles = await loadAllMigrationFiles()
    const migration = migrationFiles.find(m => m.name === name)
    if (!migration) {
      throw new Error(`Migration file "${name}" not found in any source`)
    }

    logger.info({ migration: name }, 'Rolling back by name')
    await rollbackMigration(knex, migration, record, logger)
  } finally {
    await releaseMigrationLock(knex)
  }
}

/**
 * Rollback a single migration.
 */
async function rollbackMigration(
  knex: Knex,
  migration: MigrationFile,
  record: MigrationRecord,
  logger: Logger
): Promise<void> {
  try {
    if (migration.config?.transaction === false) {
      await migration.down(knex)
    } else {
      await knex.transaction(async (trx) => {
        await migration.down(trx)
      })
    }

    await knex('_nexus_migrations').where({ id: record.id }).update({
      status: 'rolled_back',
      rolled_back_at: knex.fn.now(),
      updated_at: knex.fn.now(),
    })

    logger.info({ migration: migration.name }, 'Migration rolled back')
  } catch (err) {
    logger.error({ migration: migration.name, err }, 'Rollback failed')
    throw err
  }
}

/**
 * Load migration files from the project migrations directory only.
 * @deprecated Use loadAllMigrationFiles() for multi-source support.
 */
export async function loadMigrationFiles(): Promise<MigrationFile[]> {
  return loadMigrationFilesFromDir(getMigrationsDir(), 'project')
}

/**
 * Get next batch number.
 */
async function getNextBatch(knex: Knex): Promise<number> {
  const result = await knex('_nexus_migrations').max('batch as batch').first()
  return (result?.['batch'] ?? 0) + 1
}

/**
 * Get count of completed migrations.
 */
async function getCompletedCount(knex: Knex): Promise<number> {
  const result = await knex('_nexus_migrations')
    .where({ status: 'completed' })
    .count('* as count')
    .first()
  return Number(result?.['count'] ?? 0)
}

/**
 * Show migration status (multi-source aware).
 */
export interface MigrationStatus {
  completed: Array<{ name: string; batch: number; executionTime: number | null; status: string; source: string }>
  pending: Array<{ name: string; source: string }>
  bySource: Map<string, Array<{ name: string; status: string; batch?: number; executionTime?: number | null }>>
  total: number
  totalFiles: number
}

export async function showMigrationStatus(knexInstance?: Knex, sources?: MigrationSourceDescriptor[]): Promise<MigrationStatus> {
  const knex = knexInstance ?? getDb()

  // Load from all sources
  const migrationFiles = await loadAllMigrationFiles(sources)

  // Load executed migrations
  const executedMigrations = await knex('_nexus_migrations')
    .select<MigrationRecord[]>()
    .orderBy('executed_at', 'desc')

  const executedMap = new Map(executedMigrations.map((m) => [m.name, m]))

  if (migrationFiles.length === 0) {
    return { completed: [], pending: [], bySource: new Map(), total: 0, totalFiles: 0 }
  }

  // Group by source
  const filesBySource = new Map<string, MigrationFile[]>()
  for (const m of migrationFiles) {
    const list = filesBySource.get(m.source) ?? []
    list.push(m)
    filesBySource.set(m.source, list)
  }

  const pending: Array<{ name: string; source: string }> = []
  const completed: Array<{ name: string; batch: number; executionTime: number | null; status: string; source: string }> = []
  const bySource = new Map<string, Array<{ name: string; status: string; batch?: number; executionTime?: number | null }>>()

  for (const [source, files] of filesBySource) {
    const sourceRows: Array<{ name: string; status: string; batch?: number; executionTime?: number | null }> = []

    for (const m of files) {
      const record = executedMap.get(m.name)
      if (!record || record.status !== 'completed') {
        pending.push({ name: m.name, source })
        sourceRows.push({ name: m.name, status: 'pending' })
      } else {
        completed.push({
          name: m.name,
          batch: record.batch,
          executionTime: record.execution_time_ms,
          status: record.status,
          source,
        })
        sourceRows.push({ name: m.name, status: record.status, batch: record.batch, executionTime: record.execution_time_ms })
      }
    }

    bySource.set(source, sourceRows)
  }

  const total = await getCompletedCount(knex)

  return { completed, pending, bySource, total, totalFiles: migrationFiles.length }
}

/**
 * Rollback ALL completed migrations from a source (all batches).
 * Does NOT re-run migrations afterwards — tables are dropped.
 * @param knexInstance - Optional Knex instance (uses getDb() if not provided)
 * @param source - Source filter (e.g. 'core', 'plugin:@gzl10/nexus-plugin-tags', 'project')
 * @param sources - Optional migration sources for loading files (uses buildMigrationSources() if not provided)
 */
export async function rollbackAll(knexInstance?: Knex, source?: MigrationSourceId, sources?: MigrationSourceDescriptor[]): Promise<void> {
  const knex = knexInstance ?? getDb()

  if (process.env['NODE_ENV'] === 'production') {
    throw new Error('Cannot rollback all in production')
  }

  logger.info(source ? { source } : {}, 'Rolling back all migrations...')

  while (true) {
    const maxQuery = knex('_nexus_migrations')
      .max('batch as batch')
      .where({ status: 'completed' })
    if (source) maxQuery.where({ source })

    const result = await maxQuery.first()
    if (!result?.['batch']) break

    await rollbackLastBatch(knex, source, sources)
  }

  logger.info(source ? { source } : {}, 'All migrations rolled back')
}

/**
 * Reset database (dev only).
 * Rollback all migrations and re-run.
 * @param knexInstance - Optional Knex instance (uses getDb() if not provided)
 * @param source - Optional source filter to reset only migrations from a specific source
 */
export async function resetDatabase(knexInstance?: Knex, source?: MigrationSourceId): Promise<void> {
  const knex = knexInstance ?? getDb()

  if (process.env['NODE_ENV'] === 'production') {
    throw new Error('Cannot reset database in production')
  }

  logger.info(source ? { source } : {}, 'Resetting database...')

  await rollbackAll(knex, source)

  // Re-run migrations (filtered by source if provided)
  if (source) {
    const allSources = buildMigrationSources()
    const filtered = allSources.filter(s => s.id === source)
    if (filtered.length === 0) {
      throw new Error(`Migration source "${source}" not found. Available: ${allSources.map(s => s.id).join(', ')}`)
    }
    await runMigrations(knex, filtered)
  } else {
    await runMigrations(knex)
  }

  logger.info('Database reset complete')
}

/**
 * Undo the last completed migration: rollback + delete file + delete DB record.
 * DEV ONLY — refuses to run in production.
 */
export async function undoLastMigration(
  knexInstance?: Knex,
  source?: MigrationSourceId,
  sources?: MigrationSourceDescriptor[]
): Promise<{ name: string; filepath: string } | null> {
  if (process.env['NODE_ENV'] === 'production') {
    throw new Error('Cannot undo migrations in production')
  }

  const knex = knexInstance ?? getDb()

  await acquireMigrationLock(knex)

  try {
    // Find the last completed migration (optionally filtered by source)
    const query = knex('_nexus_migrations')
      .where({ status: 'completed' })
    if (source) query.where({ source })
    const record = await query
      .orderBy('executed_at', 'desc')
      .orderBy('batch', 'desc')
      .first<MigrationRecord>()

    if (!record) {
      logger.info(source ? { source } : {}, 'No migrations to undo')
      return null
    }

    // Load migration files to find the down() function and filepath
    const migrationFiles = await loadAllMigrationFiles(sources)
    const migration = migrationFiles.find(m => m.name === record.name)

    if (migration) {
      // Execute down() in transaction
      await knex.transaction(async (trx) => {
        await migration.down(trx)
      })
      logger.info({ migration: record.name }, 'Migration rolled back')

      // Delete the file from disk (tolerate already-deleted)
      try {
        await fs.unlink(migration.filepath)
        logger.info({ filepath: migration.filepath }, 'Migration file deleted')
      } catch (err: unknown) {
        if ((err as NodeJS.ErrnoException).code !== 'ENOENT') throw err
        logger.warn({ filepath: migration.filepath }, 'Migration file already deleted')
      }
    } else {
      logger.warn({ migration: record.name }, 'Migration file not found on disk — deleting record only')
    }

    // Delete the DB record entirely (not just mark as rolled_back)
    await knex('_nexus_migrations').where({ id: record.id }).delete()
    logger.info({ migration: record.name }, 'Migration record deleted')

    return { name: record.name, filepath: migration?.filepath ?? 'unknown' }
  } finally {
    await releaseMigrationLock(knex)
  }
}
